import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";

export type DocumentType =
  | "Formation"
  | "EinLetter"
  | "OperatingAgreement"
  | "BoiConfirmation"
  | "Msa"
  | "Sow"
  | "W9Inbound"
  | "W9Outbound"
  | "Coi"
  | "InsurancePolicy"
  | "BusinessLicense"
  | "StateAnnualReportReceipt"
  | "PriorYearTaxReturn"
  | "Paystub"
  | "Receipt"
  | "BankStatement"
  | "RetirementPlanDocument"
  | "PayrollStub"
  | "EmploymentAgreement"
  | "PhotoRelease"
  | "Other";

export interface StoredDocument {
  id: string;
  ownerUserId: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  sha256: string;
  blobContainer: string;
  blobPath: string;
  documentType: DocumentType;
  description?: string | null;
  effectiveDate?: string | null;
  expiresOn?: string | null;
  tagsCsv: string;
  version: number;
  createdAt: string;
}

export interface UploadDocumentInput {
  file: File;
  documentType: DocumentType;
  description?: string;
  tags?: string;
  effectiveDate?: string;
  expiresOn?: string;
}

const KEY = ["documents"];

export function useDocuments(type?: DocumentType) {
  return useQuery({
    queryKey: [...KEY, type ?? "all"],
    queryFn: async (): Promise<StoredDocument[]> => {
      const r = await api().get<StoredDocument[]>("/api/documents", {
        params: type ? { type } : undefined,
      });
      return r.data;
    },
  });
}

export function useUploadDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UploadDocumentInput) => {
      const fd = new FormData();
      fd.append("file", input.file);
      fd.append("documentType", input.documentType);
      if (input.description) fd.append("description", input.description);
      if (input.tags) fd.append("tags", input.tags);
      if (input.effectiveDate) fd.append("effectiveDate", input.effectiveDate);
      if (input.expiresOn) fd.append("expiresOn", input.expiresOn);
      const r = await api().post<StoredDocument>("/api/documents", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeleteDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api().delete(`/api/documents/${id}`);
      return id;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function downloadDocumentUrl(id: string): string {
  // Same-origin via Vite proxy in dev, prod uses Static Web App routing.
  return `/api/documents/${id}/download`;
}
